import { blocksClient } from '@/lib/blocks/client';
import { useAuthStore } from '@/state/store/auth';

/** Blocks OS IAM side of an employee: the login user and the roles assigned to it. */

interface IamUserSummary {
  itemId: string;
  email?: string;
}

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' ? (value as Record<string, unknown>) : {};

const assertSucceeded = (response: unknown) => {
  const { isSuccess, message } = asRecord(response);
  if (isSuccess === false)
    throw new Error(typeof message === 'string' && message ? message : 'IAM_REQUEST_FAILED');
};

// `users.list` ignores `search` (it returns the first page whatever is asked), so a lookup through it
// misses the person as soon as the tenant has more users than one page. `exists` answers by email.
export const findIamUserByEmail = async (email: string): Promise<IamUserSummary | null> => {
  const { userId } = asRecord(await blocksClient.iam.users.exists(email));
  return typeof userId === 'string' && userId ? { itemId: userId, email } : null;
};

export interface LoginUserInput {
  email: string;
  firstName: string;
  lastName: string;
  phoneNumber?: string;
  roles: string[];
  /** The organization (Blocks OS organization) this login is provisioned into; omitted means the caller's own. */
  organizationId?: string;
}

// Creating the IAM user is what sends the invitation: without a password Blocks mails the person an
// activation link, where they activate the account (no password; sign-in is SSO only). An IAM user with this email but no profile
// is an orphan from an earlier attempt whose profile save failed, so it is reused (its invitation was
// already sent) rather than failing the retry with "email already exists". `isNew` tells the caller
// whether IAM got the input just now or still carries what an earlier attempt (or the person) saved.
export const provisionLoginUser = async (
  input: LoginUserInput,
  isUserIdInUse: (userId: string) => Promise<boolean>
): Promise<{ userId: string; isNew: boolean }> => {
  const existing = await findIamUserByEmail(input.email);
  if (existing) {
    if (await isUserIdInUse(existing.itemId)) throw new Error('EMAIL_ALREADY_EXIST');
    if (input.organizationId) {
      await grantOrganizationAccess(existing.itemId, input.organizationId, input.roles);
    } else {
      await setIamUserRoles(existing.itemId, input.roles);
    }
    return { userId: existing.itemId, isNew: false };
  }

  const response = await blocksClient.iam.users.create({
    email: input.email,
    firstName: input.firstName,
    lastName: input.lastName,
    ...(input.phoneNumber ? { phoneNumber: input.phoneNumber } : {}),
    ...(input.organizationId ? { organizationId: input.organizationId } : {}),
    roles: input.roles,
  });
  assertSucceeded(response);

  const body = asRecord(response);
  const userId =
    [body.userId, body.itemId, asRecord(body.data).itemId].find(
      (value): value is string => typeof value === 'string' && value.length > 0
    ) ?? (await findIamUserByEmail(input.email))?.itemId;
  if (!userId) throw new Error('IAM_USER_NOT_CREATED');

  await ensureRoles(userId, input.roles, input.organizationId);
  return { userId, isNew: true };
};

// The chosen roles must end up on the new user, so read them back rather than trusting the create
// call. Roles IAM adds on its own are kept: the grant is the union, not a replacement.
const ensureRoles = async (
  userId: string,
  roles: string[],
  organizationId?: string
): Promise<void> => {
  if (roles.length === 0) return;
  if (organizationId) {
    const { organizationAccess } = await getIamUserOrganizationAccess(userId);
    const current =
      organizationAccess.find((organization) => organization.organizationId === organizationId)
        ?.roles ?? [];
    if (roles.every((role) => current.includes(role))) return;
    await grantOrganizationAccess(userId, organizationId, [...new Set([...current, ...roles])]);
    return;
  }
  const current = await getIamUserRoles(userId);
  if (roles.every((role) => current.includes(role))) return;
  await setIamUserRoles(userId, [...new Set([...current, ...roles])]);
};

const sameEmail = (a?: string, b?: string) => a?.trim().toLowerCase() === b?.trim().toLowerCase();

/**
 * Moves the login user to a new email when HR changes it on the profile, so the person signs in and is
 * mailed at the address the directory shows. Run before the profile is saved: if IAM refuses (email
 * taken, or the update did not take) nothing is saved and profile and login stay in step. The update
 * endpoint is documented for profile fields only, so the result is read back instead of trusted.
 * A user who never activated was invited at the old address, so the invitation is sent again.
 */
export const syncIamUserEmail = async (userId: string, email: string): Promise<void> => {
  const target = email.trim();
  const before = (await blocksClient.iam.users.get(userId)).data;
  if (!target || sameEmail(before?.email, target)) return;

  const owner = await findIamUserByEmail(target);
  if (owner && owner.itemId !== userId) throw new Error('EMAIL_ALREADY_EXIST');

  assertSucceeded(await blocksClient.iam.users.update(userId, { itemId: userId, email: target }));

  const after = (await blocksClient.iam.users.get(userId)).data;
  if (!sameEmail(after?.email, target)) throw new Error('IAM_EMAIL_UPDATE_NOT_APPLIED');

  if (before?.isVerified === false) {
    assertSucceeded(await blocksClient.auth.resendActivation({ userId }));
  }
};

/** The name, phone and photo the IAM user carries; a field left out keeps what IAM stores. */
export interface IamProfileDetails {
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  /** `''` clears the photo. */
  profileImageUrl?: string;
}

const DETAIL_KEYS = ['firstName', 'lastName', 'phoneNumber', 'profileImageUrl'] as const;

// Only the fields that really differ are sent, so an unchanged profile makes no write at all.
const changedDetails = (stored: Record<string, unknown>, details: IamProfileDetails) => {
  const changes: IamProfileDetails = {};
  DETAIL_KEYS.forEach((key) => {
    const wanted = details[key];
    const current = typeof stored[key] === 'string' ? stored[key] : '';
    if (wanted !== undefined && wanted !== current) changes[key] = wanted;
  });
  return changes;
};

/**
 * Keeps the login user's name, phone and photo in step with the profile HR saved, so the header, the
 * profile card and the mails IAM sends show the same person the directory does. `users.update` is a
 * sparse patch for these profile fields (roles and email are handled elsewhere).
 */
export const syncIamUserDetails = async (
  userId: string,
  details: IamProfileDetails
): Promise<void> => {
  const stored = asRecord((await blocksClient.iam.users.get(userId)).data);
  const changes = changedDetails(stored, details);
  if (Object.keys(changes).length === 0) return;
  assertSucceeded(await blocksClient.iam.users.update(userId, { itemId: userId, ...changes }));
};

/**
 * The signed-in person saving their own profile: the same sync as {@link syncIamUserDetails}, through
 * IAM's own-account endpoint. It is sent in the shape the profile page's edit dialog already uses
 * (the stored values with the changes on top), so nothing else on the account is touched.
 */
export const updateMyIamProfile = async (details: IamProfileDetails): Promise<void> => {
  const me = asRecord((await blocksClient.iam.me()).data);
  const changes = changedDetails(me, details);
  if (Object.keys(changes).length === 0) return;
  assertSucceeded(
    await blocksClient.iam.updateMe({
      itemId: me.itemId,
      email: me.email,
      firstName: me.firstName,
      lastName: me.lastName,
      phoneNumber: me.phoneNumber,
      profileImageUrl: me.profileImageUrl,
      memberships: me.memberships ?? [],
      ...changes,
    })
  );
};

const DEACTIVATED_PAGE_SIZE = 100;

/**
 * The user ids of every deactivated login (IAM `accountState` Deactivated; a login still waiting for
 * activation is not one). People's Alumni tab lists their profiles, the Employees tab the rest.
 */
export const getDeactivatedUserIds = async (): Promise<string[]> => {
  const ids: string[] = [];
  for (let page = 0; ; page++) {
    const response = (await blocksClient.iam.users.list({
      page,
      pageSize: DEACTIVATED_PAGE_SIZE,
      filter: { accountStates: ['Deactivated'] },
    })) as { data?: { itemId?: string; accountState?: string }[] } | undefined;
    const items = response?.data ?? [];
    items.forEach((user) => {
      // Kept to the state asked for, should IAM ever ignore the filter.
      if (user.itemId && user.accountState === 'Deactivated') ids.push(user.itemId);
    });
    if (items.length < DEACTIVATED_PAGE_SIZE) return ids;
  }
};

/** Whether the person's Blocks OS login is active. IAM owns this state; the profile keeps no copy of it. */
export const getIamUserActive = async (userId: string): Promise<boolean> =>
  (await blocksClient.iam.users.get(userId)).data?.active !== false;

/** Blocks OS side of deactivating a person: their login is disabled, so they can no longer sign in. */
export const deactivateIamUser = async (userId: string): Promise<void> => {
  assertSucceeded(await blocksClient.iam.users.deactivate({ userId }));
};

/**
 * Blocks OS side of reactivating a person. Someone who never finished activation has no password
 * yet, so - like the legacy "reactivate" - they are mailed a fresh activation link; anyone else is
 * simply switched back on.
 */
export const reactivateIamUser = async (userId: string): Promise<void> => {
  const user = (await blocksClient.iam.users.get(userId)).data;
  if (user?.isVerified === false) {
    assertSucceeded(await blocksClient.auth.resendActivation({ userId }));
    return;
  }
  // IAM's ActivateUserByAdminRequest requires a `reason`; without it the call fails with a 400.
  assertSucceeded(
    await blocksClient.iam.users.activate({ userId, reason: 'Reactivated by admin' })
  );
};

const getIamUserRoles = async (userId: string): Promise<string[]> => {
  const response = await blocksClient.iam.users.get(userId);
  return (response.data?.roles ?? []).filter((role): role is string => typeof role === 'string');
};

/** Replaces the user's role list (Blocks treats a non-empty list as the complete set). */
const setIamUserRoles = async (userId: string, roles: string[]): Promise<void> => {
  if (roles.length === 0) return;
  assertSucceeded(await blocksClient.iam.users.updateAccess({ userId, roles }));
};

export interface OrganizationAccessEntry {
  organizationId: string;
  roles: string[];
}

/**
 * Every organization the user's IAM record grants access to. A live user record
 * was checked directly against this tenant: the real shape is `organizationIds` (string[]) plus
 * `OrganizationsRoles`, an `{orgId: roles[]}` map — not the SDK-typed `memberships` array — so that map
 * is read first; `memberships` is kept as a defensive fallback in case some account shapes do return it.
 */
export const getIamUserOrganizationAccess = async (
  userId: string
): Promise<{ organizationAccess: OrganizationAccessEntry[] }> =>
  organizationAccessOf(asRecord((await blocksClient.iam.users.get(userId)).data));

/**
 * What a person's profile shows of their login, from a single IAM read: whether it is active (IAM owns
 * that state) and the organizations and roles it has access to. The signed-in person's own comes from
 * `iam/me`: `users.get` needs `blocks-iam::iam::users` (read every user), which employees don't hold,
 * and someone who is signed in is active.
 */
export const getIamUserSummary = async (
  userId: string
): Promise<{ isActive: boolean; organizationAccess: OrganizationAccessEntry[] }> => {
  if (userId === useAuthStore.getState().user?.itemId) {
    return {
      isActive: true,
      ...organizationAccessOf(asRecord((await blocksClient.iam.me()).data)),
    };
  }
  const record = asRecord((await blocksClient.iam.users.get(userId)).data);
  return { isActive: record.active !== false, ...organizationAccessOf(record) };
};

const organizationAccessOf = (
  record: Record<string, unknown>
): { organizationAccess: OrganizationAccessEntry[] } => {
  const organizationsRoles = asRecord(record.OrganizationsRoles);
  const organizationsRolesIds = Object.keys(organizationsRoles);
  if (organizationsRolesIds.length > 0) {
    return {
      organizationAccess: organizationsRolesIds.map((organizationId) => ({
        organizationId,
        roles: Array.isArray(organizationsRoles[organizationId])
          ? (organizationsRoles[organizationId] as unknown[]).filter(
              (role): role is string => typeof role === 'string'
            )
          : [],
      })),
    };
  }

  const memberships = Array.isArray(record.memberships)
    ? (record.memberships as Record<string, unknown>[])
    : [];
  if (memberships.length > 0) {
    return {
      organizationAccess: memberships
        .map((membership) => ({
          organizationId:
            typeof membership.organizationId === 'string' ? membership.organizationId : '',
          roles: Array.isArray(membership.roles)
            ? membership.roles.filter((role): role is string => typeof role === 'string')
            : [],
        }))
        .filter((entry) => entry.organizationId),
    };
  }

  // No per-organization map on the record: the flat `roles` are this user's access in their only organization.
  const roles = Array.isArray(record.roles)
    ? record.roles.filter((role): role is string => typeof role === 'string')
    : [];
  const [organizationId] = Array.isArray(record.organizationIds) ? record.organizationIds : [];
  return {
    organizationAccess:
      typeof organizationId === 'string' && roles.length > 0 ? [{ organizationId, roles }] : [],
  };
};

/** Grants (or replaces) a user's role list within one organization; a no-op if there is nothing to grant. */
const grantOrganizationAccess = async (
  userId: string,
  organizationId: string,
  roles: string[]
): Promise<void> => {
  if (roles.length === 0) return;
  assertSucceeded(await blocksClient.iam.users.updateAccess({ userId, roles, organizationId }));
};

/** Revokes a user's access to one organization entirely. */
const revokeOrganizationAccess = async (userId: string, organizationId: string): Promise<void> => {
  assertSucceeded(await blocksClient.iam.users.revokeAccess({ userId, organizationId }));
};

/**
 * True when `after` takes anything away from `before`: an organization dropped, or a role removed within one.
 * Only a downgrade needs the user's sessions revoked; added access just waits for their next token.
 */
export const isOrganizationAccessReduced = (
  before: OrganizationAccessEntry[],
  after: OrganizationAccessEntry[]
): boolean => {
  const afterByOrg = new Map(
    after.map((organization) => [organization.organizationId, organization.roles])
  );
  return before.some((organization) => {
    const roles = afterByOrg.get(organization.organizationId);
    return !roles || organization.roles.some((role) => !roles.includes(role));
  });
};

interface IamSession {
  sessionId: string;
  userId?: string | null;
}

/**
 * Ends every sign-in session of another user, so a role change takes effect without waiting for them to sign
 * out (their current access token stays valid until it expires, but it can no longer be refreshed). Mirrors the
 * portal: `GET /security/sessions?uid=` lists the user's sessions, then `POST /security/sessions/{sessionId}/revoke`.
 * The param must be `uid`: an unknown one is ignored and the caller's own sessions come back, so sessions are
 * also filtered to `userId` - a list scoped to the caller can never revoke the admin's own sessions.
 */
export const revokeIamUserSessions = async (userId: string): Promise<void> => {
  const res = await blocksClient.http.request<IamSession[] | { data?: IamSession[] }>(
    '/iam/v4/security/sessions',
    { query: { uid: userId } }
  );
  const sessions = (Array.isArray(res) ? res : (res?.data ?? [])).filter(
    (session) => session.userId === userId
  );

  // Every session is attempted even if one fails, so a single bad session never leaves the rest signed in.
  const results = await Promise.allSettled(
    sessions.map(async (session) =>
      assertSucceeded(
        await blocksClient.http.request(
          `/iam/v4/security/sessions/${encodeURIComponent(session.sessionId)}/revoke`,
          { method: 'POST', body: { userId } }
        )
      )
    )
  );
  const failure = results.find((result) => result.status === 'rejected');
  if (failure) throw failure.reason;
};

const sameRoleList = (a: string[], b: string[]) =>
  a.length === b.length && a.every((role) => b.includes(role));

/**
 * Grants every organization in `after` whose role list changed from `before`, and revokes any organization in
 * `before` that `after` no longer includes, so a user's IAM access always matches what was chosen.
 */
export const syncOrganizationAccess = async (
  userId: string,
  before: OrganizationAccessEntry[],
  after: OrganizationAccessEntry[]
): Promise<void> => {
  const beforeByOrg = new Map(
    before.map((organization) => [organization.organizationId, organization.roles])
  );
  const afterOrgIds = new Set(after.map((organization) => organization.organizationId));

  await Promise.all([
    ...after
      .filter(
        (organization) =>
          !sameRoleList(beforeByOrg.get(organization.organizationId) ?? [], organization.roles)
      )
      .map((organization) =>
        grantOrganizationAccess(userId, organization.organizationId, organization.roles)
      ),
    ...before
      .filter((organization) => !afterOrgIds.has(organization.organizationId))
      .map((organization) => revokeOrganizationAccess(userId, organization.organizationId)),
  ]);
};
